import * as types from '../types';
import { isModuleSubscriptionProduct, isUltraProduct } from '../types/Product';
import { Species } from '../types/gql-op-types';
import { FiCountry } from '../contexts/LocaleContext';
import { isAxiosError, webApiClient } from './fi-api/apiUtils';
import { logInternalError } from './errors';
import { getProductsBySku } from './product';

export function cartHasSomePhysicalProducts(cart: types.Cart, products: types.IProduct[]) {
  const productMap = getProductsBySku(products);
  return Object.values(cart.cartItems).some((cartItem) => {
    const product = productMap.get(cartItem.lineItem.sku);
    return product && product.physical;
  });
}

export function isBundleCartItem(cartItem: types.CartItem): cartItem is types.BundleCartItem {
  return 'subscriptionLineItem' in cartItem;
}

/**
 * Returns true when every bundle in the cart has cat species selected.
 * Non-bundle items (gift cards, supplements, etc.) are not considered.
 * Used to skip the supplements upsell — supplements are dog-only products.
 */
export function allBundlesAreCatSpecies(cart: types.Cart): boolean {
  const bundles = Object.values(cart.cartItems).filter(isBundleCartItem);
  if (bundles.length === 0) return false;
  return bundles.every((item) => item.species === Species.CAT);
}

export function isGiftCardCartItem(cartItem: types.CartItem): cartItem is types.GiftCardCartItem {
  return 'giftCardDelivery' in cartItem;
}

export function isForModuleCartItem(
  cartItem: types.CartItem,
): cartItem is types.ForModuleBundleCartItem | types.ForModuleCartItem {
  return 'forModuleId' in cartItem;
}

export function isUpgradeCartItem(cartItem: types.CartItem): cartItem is types.Series3UpgradeCartItem {
  return isBundleCartItem(cartItem) && isForModuleCartItem(cartItem);
}

export function skusForCartItem(cartItem: types.CartItem) {
  if (isBundleCartItem(cartItem)) {
    const addonSkus = cartItem.addonLineItems?.map((lineItem) => lineItem.sku) ?? [];
    return [cartItem.lineItem.sku, cartItem.subscriptionLineItem.sku, ...addonSkus];
  }

  return [cartItem.lineItem.sku];
}

export function giftCardCartItems(cart: types.Cart) {
  return Object.values(cart.cartItems).filter(isGiftCardCartItem);
}

export function cartHasItems(cart: types.Cart) {
  return Object.keys(cart.cartItems).length > 0;
}

/**
 * Shared predicate: returns [id, cartItem] entries for bundles with a free trial subscription.
 * Used by the free-trial helpers below to avoid duplicating the filter logic.
 */
function freeTrialBundleEntries(cart: types.Cart, products: types.IProduct[]) {
  const productMap = getProductsBySku(products);
  return Object.entries(cart.cartItems).filter(([, item]) => {
    if (!isBundleCartItem(item)) return false;
    const sub = productMap.get(item.subscriptionLineItem.sku);
    return sub && isModuleSubscriptionProduct(sub) && sub.freeTrial;
  });
}

export function cartContainsOnlyFreeTrialBundles(cart: types.Cart, products: types.IProduct[]): boolean {
  const bundles = Object.values(cart.cartItems).filter(isBundleCartItem);
  if (bundles.length === 0) return false;
  return freeTrialBundleEntries(cart, products).length === bundles.length;
}

export function getFreeTrialCartItemIds(cart: types.Cart, products: types.IProduct[]): string[] {
  return freeTrialBundleEntries(cart, products).map(([id]) => id);
}

export function cartContainsAnyFreeTrialBundle(cart: types.Cart, products: types.IProduct[]): boolean {
  return freeTrialBundleEntries(cart, products).length > 0;
}

/**
 * Returns true when the cart contains at least one Fi Ultra product, whether purchased as a
 * standalone hardware line item or bundled with a subscription.
 * Used to skip the post-checkout supplements upsell for Ultra purchases.
 */
export function cartContainsUltra(cart: types.Cart, products: types.IProduct[]): boolean {
  const productMap = getProductsBySku(products);
  return Object.values(cart.cartItems).some((item) =>
    skusForCartItem(item).some((sku) => {
      const product = productMap.get(sku);
      return product ? isUltraProduct(product) : false;
    }),
  );
}

/** true when the cart has at least one bundle with a paid (non-free-trial) subscription */
export function cartContainsPaidSubscription(cart: types.Cart, products: types.IProduct[]): boolean {
  const productMap = getProductsBySku(products);
  return Object.values(cart.cartItems).some((item) => {
    if (!isBundleCartItem(item)) return false;
    const sub = productMap.get(item.subscriptionLineItem.sku);
    return sub && isModuleSubscriptionProduct(sub) && !sub.freeTrial;
  });
}

/**
 * Validates whether items in the cart are available for purchase in the
 * given country. Returns IDs of invalid cart items so the caller can
 * remove them and notify the user.
 */
export async function validateCart(cart: types.Cart, country: FiCountry): Promise<string[]> {
  try {
    await webApiClient.post('/api/ecommerce/validate-cart', {
      cartItems: Object.values(cart.cartItems),
      countryCode: country,
    });
    return [];
  } catch (err) {
    if (isAxiosError(err) && err.response?.data?.error?.cartItemIds) {
      return err.response.data.error.cartItemIds;
    }
    logInternalError(err, { tags: { source: 'validateCart' } });
    throw err;
  }
}
